import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";

// @material-ui/core components
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import CustomInput from "components/CustomInput/CustomInput";

// Loader
import { ThreeDots } from "react-loader-spinner";

const styles = () => ({
  confirmButton: {
    minWidth: "120px", // Ensure consistent width
    width: "120px", // Fixed width to prevent changes
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px", // Consistent padding
  },
  statusSection: {
    margin: "10px 0",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statusLabel: {
    fontSize: "14px",
    fontWeight: "300",
    color: "#333",
    whiteSpace: "nowrap",
    marginLeft: "6px",
  },
  statusBadgesContainer: {
    display: "flex",
    gap: "10px",
    marginLeft: "5px",
    alignItems: "center", // Ensure vertical centering
  },
  statusBadge: {
    cursor: "pointer",
    transition: "all 0.2s ease !important",
    opacity: "0.4",
    fontWeight: "300 !important",
    padding: "3px 12px !important",
    borderRadius: "20px !important",
    border: "none",
    color: "#ffffff",
    backgroundColor: "transparent",
    textAlign: "center",
    boxShadow: "none !important",
    "&:hover": {
      opacity: "0.9",
      transform: "scale(1.1)",
    },
  },
  statusBadgeSelected: {
    opacity: "1",
    transform: "scale(1.05)",
  },
  statusBadgeSuccess: {
    backgroundColor: "#4caf50 !important",
  },
  statusBadgeWarning: {
    backgroundColor: "#ff9800 !important",
  },
  statusBadgeDanger: {
    backgroundColor: "#f44336 !important",
  },
});

const AddUpdateDialog = ({
  // Dialog state
  addUpdateModalOpen,
  setAddUpdateModalOpen,
  // Data
  initiatives,
  selectedInitiative,
  // Loading state
  initiativesBusy,
  // Functions
  saveAddUpdate,
  // Styles and theme
  classes,
  t,
  clientColor,
}) => {
  const useStyles = makeStyles(styles);
  const dialogClasses = useStyles();

  // Local state for the dialog
  const [addUpdateText, setAddUpdateText] = useState("");
  const [selectedTrafficLight, setSelectedTrafficLight] = useState(1);
  const addUpdateTextRef = useRef(null);

  // Helper function to check if text is non-empty
  const isNonEmpty = (text) => text.trim() !== "";

  // Get the default traffic light from the last update
  const getDefaultTrafficLight = useCallback(() => {
    if (!selectedInitiative || selectedInitiative === "new") return 1;

    const followUpData = initiatives[selectedInitiative]?.follow_up;
    if (!followUpData || followUpData.length === 0) return 1;

    // Get the traffic light from the most recent update (first in the array)
    const lastUpdate = followUpData[0];
    return lastUpdate.traffic_light > 0 ? lastUpdate.traffic_light : 1;
  }, [selectedInitiative, initiatives]);

  // Check if the add update form is ready to submit
  const isAddUpdateReady = useCallback(() => {
    return isNonEmpty(addUpdateText);
  }, [addUpdateText]);

  // Handle text change
  const handleTextChange = useCallback((event) => {
    setAddUpdateText(event.target.value);
  }, []);

  // Handle text blur (trim whitespace)
  const handleTextBlur = useCallback(() => {
    setAddUpdateText((text) => text.trim());
  }, []);

  // Handle save with local text
  const handleSaveAddUpdate = useCallback(() => {
    saveAddUpdate(addUpdateText, selectedTrafficLight);
  }, [saveAddUpdate, addUpdateText, selectedTrafficLight]);

  // Handle traffic light selection
  const handleTrafficLightSelect = useCallback((trafficLight) => {
    setSelectedTrafficLight(trafficLight);
  }, []);

  // Reset text when dialog opens/closes
  useEffect(() => {
    setAddUpdateText("");
    setSelectedTrafficLight(getDefaultTrafficLight());
  }, [addUpdateModalOpen, getDefaultTrafficLight]);
  // Auto-focus the text input when dialog opens
  useEffect(() => {
    if (addUpdateModalOpen && addUpdateTextRef.current) {
      setTimeout(() => {
        if (addUpdateTextRef.current) {
          // Try multiple approaches to focus the input
          if (addUpdateTextRef.current.focus) {
            // Direct focus if the ref points to the input element
            addUpdateTextRef.current.focus();
          } else if (addUpdateTextRef.current.querySelector) {
            // Try to find the textarea within the component
            const textarea = addUpdateTextRef.current.querySelector("textarea");
            if (textarea) {
              textarea.focus();
            }
          }
        }
      }, 150);
    }
  }, [addUpdateModalOpen, addUpdateTextRef]);

  return (
    <>
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={addUpdateModalOpen}
        keepMounted
        onClose={() => setAddUpdateModalOpen(false)}
        aria-labelledby="classic-modal-slide-title"
        aria-describedby="classic-modal-slide-description"
        fullWidth
        maxWidth={"md"}
      >
        <ModalHeader
          id="content-modal-slide-title"
          title={t("initiatives.add_update")}
          subtitle={initiatives[selectedInitiative]?.short_description}
          onClose={() => setAddUpdateModalOpen(false)}
          closeDisabled={initiativesBusy}
          closeLabel={t("initiatives.close")}
          clientColor={clientColor}
        />
        <DialogContent
          id="classic-modal-slide-description"
          className={classes.modalBody}
        >
          <GridContainer alignItems={"center"}>
            {/* Status Selection Section */}
            <GridItem xs={12}>
              <div className={dialogClasses.statusSection}>
                <div className={dialogClasses.statusLabel}>
                  {t("initiatives.add_update_modal.status")}
                </div>
                <div className={dialogClasses.statusBadgesContainer}>
                  <Button
                    className={cx(
                      dialogClasses.statusBadge,
                      dialogClasses.statusBadgeSuccess,
                      {
                        [dialogClasses.statusBadgeSelected]:
                          selectedTrafficLight === 1,
                      },
                    )}
                    onClick={() => handleTrafficLightSelect(1)}
                    disabled={initiativesBusy}
                  >
                    {t("initiatives.add_update_modal.on_track")}
                  </Button>
                  <Button
                    className={cx(
                      dialogClasses.statusBadge,
                      dialogClasses.statusBadgeWarning,
                      {
                        [dialogClasses.statusBadgeSelected]:
                          selectedTrafficLight === 2,
                      },
                    )}
                    onClick={() => handleTrafficLightSelect(2)}
                    disabled={initiativesBusy}
                  >
                    {t("initiatives.add_update_modal.at_risk")}
                  </Button>
                  <Button
                    className={cx(
                      dialogClasses.statusBadge,
                      dialogClasses.statusBadgeDanger,
                      {
                        [dialogClasses.statusBadgeSelected]:
                          selectedTrafficLight === 3,
                      },
                    )}
                    onClick={() => handleTrafficLightSelect(3)}
                    disabled={initiativesBusy}
                  >
                    {t("initiatives.add_update_modal.off_track")}
                  </Button>
                </div>
              </div>
            </GridItem>

            <GridItem xs={12}>
              <CustomInput
                labelText={t("initiatives.add_update_modal.update_description")}
                id="add_update_description"
                ref={addUpdateTextRef}
                value={addUpdateText}
                onChange={handleTextChange}
                formControlProps={{
                  fullWidth: true,
                  style: {
                    paddingTop: "0",
                    marginBottom: "0",
                    marginTop: "10px",
                  },
                }}
                inputProps={{
                  autoComplete: "off",
                  disabled: initiativesBusy,
                  maxLength: 1000,
                  autoFocus: addUpdateModalOpen,
                }}
                client
                multiline
                onBlur={handleTextBlur}
                disabled={initiativesBusy}
              />
            </GridItem>
          </GridContainer>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <Button
            onClick={() => setAddUpdateModalOpen(false)}
            color="danger"
            simple
            disabled={initiativesBusy}
          >
            {t("initiatives.add_update_modal.cancel")}
          </Button>
          <Button
            onClick={handleSaveAddUpdate}
            color="client"
            simple
            disabled={!isAddUpdateReady() || initiativesBusy}
            className={dialogClasses.confirmButton}
          >
            {initiativesBusy ? (
              <ThreeDots
                height="18px"
                width="18px"
                color={clientColor}
                ariaLabel="loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            ) : (
              t("initiatives.add_update_modal.confirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

AddUpdateDialog.propTypes = {
  // Dialog state
  addUpdateModalOpen: PropTypes.bool.isRequired,
  setAddUpdateModalOpen: PropTypes.func.isRequired,
  // Data
  initiatives: PropTypes.object.isRequired,
  selectedInitiative: PropTypes.string.isRequired,
  // Loading state
  initiativesBusy: PropTypes.bool.isRequired,
  // Functions
  saveAddUpdate: PropTypes.func.isRequired,
  // Styles and theme
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
};

export default AddUpdateDialog;
